import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { Plus, Check, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { ProductCard } from "@/components/site/ProductCard";
import { fetchProductByHandle, fallbackImage, fetchAllProducts } from "@/lib/catalog";
import { useInquiry } from "@/lib/inquiry-store";
import { seo } from "@/lib/seo";

const CERT_LOGOS: Array<{ match: RegExp; src: string; alt: string; className?: string }> = [
  { match: /usda/i, src: "/assets/images/USDA-organic.svg", alt: "USDA Organic certified" },
  {
    match: /jas organic/i,
    src: "/assets/images/jas-organic.svg",
    alt: "JAS Organic certified",
    className: "h-[40px]",
  },
  {
    match: /jas oco/i,
    src: "/assets/images/jas-organic.svg",
    alt: "JAS OCO certified",
    className: "h-[40px]",
  },
  { match: /kosher/i, src: "/assets/images/kosher.svg", alt: "Kosher certified" },
  { match: /halal/i, src: "/assets/images/npo-halal-japan.png", alt: "Halal certified" },
];

const qo = (handle: string) =>
  queryOptions({ queryKey: ["product", handle], queryFn: () => fetchProductByHandle(handle) });

const qoAll = queryOptions({ queryKey: ["products"], queryFn: () => fetchAllProducts() });

export const Route = createFileRoute("/products/$handle")({
  loader: async ({ context, params }) => {
    const p = await context.queryClient.ensureQueryData(qo(params.handle));
    if (!p) throw notFound();
    await context.queryClient.ensureQueryData(qoAll);
    return p;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: `${loaderData.name} – Wholesale QT Foods`,
          description:
            loaderData.short_description ??
            `${loaderData.name} — wholesale & private-label from QT Foods.`,
          image: loaderData.image_url ?? undefined,
        })
      : [],
  }),
  component: Page,
  notFoundComponent: () => (
    <SiteLayout breadcrumbs={[{ label: "Catalog", href: "/collections" }, { label: "Not Found" }]}>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="font-serif text-4xl">Product not found</h1>
        <Link to="/collections" className="mt-6 inline-block text-primary hover:underline">
          ← Back to catalog
        </Link>
      </div>
    </SiteLayout>
  ),
});

function Page() {
  const { handle } = Route.useParams();
  const { data: rawProduct } = useSuspenseQuery(qo(handle));
  const { data: allProducts } = useSuspenseQuery(qoAll);
  const { add } = useInquiry();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  if (!rawProduct) return null;

  const product = { ...rawProduct, specs: rawProduct.specs ? { ...rawProduct.specs } : undefined };
  if (product.specs && typeof product.specs.tasting_color === "number") {
    const colorScore = product.specs.tasting_color;
    if (colorScore <= 2) product.specs.color = "Dark Green";
    else if (colorScore >= 4) product.specs.color = "Vibrant Green";
    else product.specs.color = "Green";
  } else if (product.specs) {
    delete product.specs.color;
  }

  const img = product.image_url || fallbackImage(product.categories?.handle);
  const allImages = [
    img,
    ...(product.specs?.images ? product.specs.images.split(",").map((s: string) => s.trim()) : []),
  ];
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [product.handle]);

  const displayImage = allImages[selectedImageIndex] || img;

  const onAdd = () => {
    add({
      productHandle: product.handle,
      productName: product.name,
      categoryName: product.categories?.name,
      imageUrl: img,
      quantity: qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const currentIndex = allProducts.findIndex((p: any) => p.handle === product.handle);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;

  return (
    <SiteLayout
      breadcrumbs={[
        { label: "Catalog", href: "/collections" },
        ...(product.categories
          ? [
              {
                label: product.categories.name,
                href: "/collections/$handle",
                params: { handle: product.categories.handle },
              },
            ]
          : []),
        { label: product.name },
      ]}
    >
      <section className="mx-auto max-w-7xl px-5 pt-8 pb-12 lg:px-8 lg:pb-16">
        <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] md:gap-16">
          <div className="md:sticky md:top-24 md:self-start space-y-4">
            <div className="aspect-square overflow-hidden bg-transparent rounded-sm">
              <img
                src={displayImage}
                alt={product.name}
                title={product.name}
                width={600}
                height={600}
                className="h-full w-full object-cover"
              />
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {allImages.map((src: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImageIndex(i)}
                    className={`h-20 w-20 flex-shrink-0 overflow-hidden rounded-sm border-2 transition-colors ${i === selectedImageIndex ? "border-primary" : "border-transparent hover:border-primary/50"}`}
                  >
                    <img
                      src={src}
                      alt={`${product.name} view ${i + 1}`}
                      title={`${product.name} view ${i + 1}`}
                      width={80}
                      height={80}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-4">
              {product.categories ? (
                <Link
                  to="/collections/$handle"
                  params={{ handle: product.categories.handle }}
                  className="text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-primary transition-colors"
                >
                  {product.categories.name}
                </Link>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-2">
                {prevProduct ? (
                  <Link
                    to="/products/$handle"
                    params={{ handle: prevProduct.handle }}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    title={`Previous: ${prevProduct.name}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Link>
                ) : (
                  <div className="h-8 w-8 opacity-20 flex items-center justify-center rounded-full border border-border">
                    <ChevronLeft className="h-4 w-4" />
                  </div>
                )}
                {nextProduct ? (
                  <Link
                    to="/products/$handle"
                    params={{ handle: nextProduct.handle }}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    title={`Next: ${nextProduct.name}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                ) : (
                  <div className="h-8 w-8 opacity-20 flex items-center justify-center rounded-full border border-border">
                    <ChevronRight className="h-4 w-4" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-6 items-center justify-between">
              <h1 className="font-serif text-4xl md:text-5xl leading-tight">{product.name}</h1>

              {(() => {
                const certsStr = String(product.specs?.certifications ?? "");
                if (!certsStr) return null;

                const certsList = certsStr
                  .split(",")
                  .map((c) => c.trim())
                  .filter(Boolean);
                if (certsList.length === 0) return null;

                return (
                  <div className="flex flex-wrap gap-3 items-center justify-end">
                    {certsList.map((cert) => {
                      const logo = CERT_LOGOS.find((l) => l.match.test(cert));
                      if (logo) {
                        return (
                          <img
                            key={logo.src}
                            src={logo.src}
                            alt={logo.alt}
                            title={cert}
                            width={48}
                            height={48}
                            className={`${logo.className || "h-12"} object-contain`}
                          />
                        );
                      }
                      return (
                        <span
                          key={cert}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-secondary text-secondary-foreground border border-border whitespace-nowrap"
                        >
                          {cert}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {product.short_description && (
              <p className="mt-5 text-base text-foreground/80">{product.short_description}</p>
            )}

            {product.description && (
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
                {product.description}
              </p>
            )}

            {(() => {
              const s = product.specs || {};
              const hasTasting =
                s.tasting_color != null || s.tasting_umami != null || s.tasting_astringency != null;

              if (!hasTasting) return null;

              const rows: Array<{ letter: string; label: string; sub: string; value: number }> = [
                {
                  letter: "C",
                  label: "Color",
                  sub: "Visual intensity of green color",
                  value: Number(s.tasting_color) || 0,
                },
                {
                  letter: "U",
                  label: "Umami",
                  sub: "Natural sweetness and richness",
                  value: Number(s.tasting_umami) || 0,
                },
                {
                  letter: "A",
                  label: "Astringency",
                  sub: "Pleasant bitterness and sharpness",
                  value: Number(s.tasting_astringency) || 0,
                },
              ];
              return (
                <div className="mt-8 border-t border-border pt-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Tasting profile
                  </p>
                  <ul className="mt-4 space-y-3">
                    {rows.map((r) => (
                      <li key={r.letter} className="flex items-center gap-4">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 font-serif text-sm text-primary">
                          {r.letter}
                        </span>
                        <div className="w-28 flex-shrink-0">
                          <p className="text-sm font-medium leading-tight">{r.label}</p>
                          <p className="text-[11px] leading-tight text-muted-foreground">{r.sub}</p>
                        </div>
                        <div className="flex flex-1 gap-1">
                          {[1, 2, 3, 4, 5].map((n) => {
                            let fill = 0;
                            if (r.value >= n) fill = 100;
                            else if (r.value > n - 1) fill = (r.value - (n - 1)) * 100;
                            return (
                              <div
                                key={n}
                                className="h-2 flex-1 overflow-hidden rounded-sm bg-muted relative"
                              >
                                <div
                                  className="absolute left-0 top-0 h-full bg-primary"
                                  style={{ width: `${fill}%` }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <span className="w-8 text-right text-xs tabular-nums text-muted-foreground">
                          {r.value}/5
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {product.specs?.recommended_for && (
              <div className="mt-8 border-t border-border pt-6">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-foreground">
                  Recommended For
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {product.specs.recommended_for}
                </p>
              </div>
            )}

            {product.specs?.includes && (
              <div className="mt-6 border-t border-border pt-6">
                <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-foreground">
                  Includes
                </p>
                <ul className="space-y-1.5 text-sm text-muted-foreground leading-relaxed">
                  {product.specs.includes.split(",").map((item: string, i: number) => (
                    <li key={i}>{item.trim()}</li>
                  ))}
                </ul>
              </div>
            )}

            {product.specs &&
              Object.keys(product.specs).filter((k) => {
                const val = product.specs![k];
                if (val == null || val === "" || val === undefined) return false;
                if (
                  k.startsWith("tasting_") ||
                  k === "certifications" ||
                  k === "recommended_for" ||
                  k === "includes" ||
                  k === "images" ||
                  k === "tea_type" ||
                  k === "product"
                )
                  return false;
                return true;
              }).length > 0 && (
                <dl className="mt-8 divide-y divide-border border-t border-border">
                  {Object.entries(product.specs)
                    .filter(([k, v]) => {
                      if (v == null || v === "" || v === undefined) return false;
                      if (
                        k.startsWith("tasting_") ||
                        k === "certifications" ||
                        k === "recommended_for" ||
                        k === "includes" ||
                        k === "images" ||
                        k === "tea_type" ||
                        k === "product"
                      )
                        return false;
                      return true;
                    })
                    .map(([k, v]) => {
                      const label = k === "moq" ? "MOQ" : k.replace(/_/g, " ");
                      return (
                        <div key={k} className="flex justify-between gap-6 py-3 text-sm">
                          <dt
                            className={
                              k === "moq"
                                ? "text-muted-foreground"
                                : "capitalize text-muted-foreground"
                            }
                          >
                            {label}
                          </dt>
                          <dd className="text-right">{String(v)}</dd>
                        </div>
                      );
                    })}
                </dl>
              )}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="hidden items-center border border-border">
                <button
                  className="px-3 py-2 text-lg hover:bg-secondary"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-14 bg-transparent py-2 text-center text-sm focus:outline-none"
                />
                <button
                  className="px-3 py-2 text-lg hover:bg-secondary"
                  onClick={() => setQty((q) => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <button
                onClick={onAdd}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-medium uppercase tracking-[0.15em] text-primary-foreground hover:bg-primary/90"
              >
                {added ? (
                  <>
                    <Check className="h-4 w-4" /> Added to inquiry
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" /> Add to Inquiry
                  </>
                )}
              </button>
            </div>
            <Link
              to="/inquiry"
              className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              Review your inquiry <ArrowRight className="h-4 w-4" />
            </Link>

            <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
              Wholesale-only. Pricing, MOQ and lead times are confirmed by quote — add this product
              to your inquiry list and our team will respond within 1–2 business days.
            </p>

            <div className="mt-8 border-t border-border pt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Lead time &amp; availability
              </p>
              <ul className="mt-4 space-y-2 text-xs leading-relaxed text-muted-foreground">
                <li>• Items or sizes not listed in this catalog may be available upon request.</li>
                <li>
                  • Standard lead time from order confirmation to shipment readiness is
                  approximately 1.5 months.
                </li>
                <li>
                  • International shipping time from Japan is additional and varies by destination
                  and shipping method.
                </li>
                <li>
                  • Prices are subject to change due to fluctuations in new harvest tea prices.
                </li>
                <li>
                  • Matcha from upcoming harvests is produced in limited quantities — early
                  reservation is strongly recommended, as supply may not meet demand if orders are
                  placed later.
                </li>
                <li>• Stock availability may vary depending on season and order volume.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation and Similar Products */}
      {(() => {
        const similarProducts = allProducts
          .filter(
            (p: any) =>
              p.handle !== product.handle && p.specs?.tea_type === product.specs?.tea_type,
          )
          .slice(0, 4);

        return (
          <div className="mx-auto max-w-7xl px-5 pb-24">
            <div className="flex items-center justify-between border-t border-border py-8">
              {prevProduct ? (
                <Link
                  to="/products/$handle"
                  params={{ handle: prevProduct.handle }}
                  className="group flex items-center gap-3 text-sm font-medium hover:text-primary transition-colors"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground transition-transform group-hover:-translate-x-1">
                    <ChevronLeft className="h-5 w-5" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-0.5">
                      Previous
                    </p>
                    <p className="font-serif">{prevProduct.name}</p>
                  </div>
                </Link>
              ) : (
                <div />
              )}

              {nextProduct && (
                <Link
                  to="/products/$handle"
                  params={{ handle: nextProduct.handle }}
                  className="group flex items-center gap-3 text-sm font-medium hover:text-primary text-right transition-colors"
                >
                  <div className="hidden sm:block">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-0.5">
                      Next
                    </p>
                    <p className="font-serif">{nextProduct.name}</p>
                  </div>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground transition-transform group-hover:translate-x-1">
                    <ChevronRight className="h-5 w-5" />
                  </div>
                </Link>
              )}
            </div>

            {similarProducts.length > 0 && (
              <div className="mt-16">
                <h2 className="font-serif text-3xl mb-8">You may also like</h2>
                <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:grid-cols-4 sm:gap-x-8">
                  {similarProducts.map((p: any) => (
                    <ProductCard key={p.handle} product={p} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </SiteLayout>
  );
}
